import Layout from "../components/layout/LandingPageLayout";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  useTheme,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PeopleIcon from "@mui/icons-material/People";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import PublicIcon from "@mui/icons-material/Public";
import SecurityIcon from "@mui/icons-material/Security";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import { Link } from "react-router-dom";

const EinvoiceInfo = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <BusinessIcon sx={{ fontSize: 48 }} />,
      title: "Multiple Company Management",
      description:
        "Seamlessly manage invoices for multiple businesses from a single dashboard. Switch between companies effortlessly.",
    },
    {
      icon: <DashboardCustomizeIcon sx={{ fontSize: 48 }} />,
      title: "Customizable Layouts",
      description:
        "Choose from multiple professional invoice templates. Customize colors, logos, and layouts to match your brand identity.",
    },
    {
      icon: <EmailIcon sx={{ fontSize: 48 }} />,
      title: "Email Invoice Delivery",
      description:
        "Send invoices directly to clients via email with automated reminders and delivery tracking.",
    },
    {
      icon: <WhatsAppIcon sx={{ fontSize: 48 }} />,
      title: "WhatsApp Integration",
      description:
        "Share invoices instantly through WhatsApp with one click. Keep your clients connected on their preferred platform.",
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 48 }} />,
      title: "Vendor & Customer Management",
      description:
        "Maintain comprehensive databases of vendors and customers with detailed profiles and transaction history.",
    },
    {
      icon: <AutoAwesomeIcon sx={{ fontSize: 48 }} />,
      title: "AI-Powered Assistance",
      description:
        "Get intelligent suggestions for pricing, payment terms, and invoice optimization powered by advanced AI.",
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 48 }} />,
      title: "GST Compliance",
      description:
        "Fully compliant with GST regulations. Automatic tax calculations and GST-ready invoice formats.",
    },
    {
      icon: <AccountBalanceWalletIcon sx={{ fontSize: 48 }} />,
      title: "Multiple Payment Methods",
      description:
        "Accept payments via cash, UPI, credit/debit cards. Give your customers flexible payment options.",
    },
    {
      icon: <CreditCardIcon sx={{ fontSize: 48 }} />,
      title: "Card Payment Support",
      description:
        "Integrated multi-card payment gateway for secure and instant online transactions.",
    },
  ];

  const benefits = [
    { icon: <FlashOnIcon />, text: "Lightning-fast invoice generation" },
    { icon: <PublicIcon />, text: "Access from anywhere, anytime" },
    { icon: <SecurityIcon />, text: "Bank-level security" },
    { icon: <TrendingUpIcon />, text: "Real-time analytics & reports" },
  ];

  const paymentMethods = [
    {
      icon: <AccountBalanceWalletIcon sx={{ fontSize: 56 }} />,
      label: "Cash",
      color: theme.palette.success.main,
    },
    {
      icon: <SmartphoneIcon sx={{ fontSize: 56 }} />,
      label: "UPI",
      color: theme.palette.secondary.main,
    },
    {
      icon: <CreditCardIcon sx={{ fontSize: 56 }} />,
      label: "Credit Card",
      color: theme.palette.primary.main,
    },
    {
      icon: <CreditCardIcon sx={{ fontSize: 56 }} />,
      label: "Debit Card",
      color: theme.palette.warning.main,
    },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 50%, ${theme.palette.secondary.dark} 100%)`,
          color: theme.palette.primary.contrastText,
          py: 10,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: "bold",
                mb: 3,
                fontSize: { xs: "2.5rem", md: "3.75rem" },
              }}
            >
              Smart E-Invoice System
            </Typography>
            <Typography
              variant="h5"
              sx={{ mb: 4, opacity: 0.9, maxWidth: "800px", mx: "auto" }}
            >
              Transform your billing process with our intelligent invoicing
              platform. Fast, secure, and designed for modern businesses.
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                component={Link}
                to="/login"
                variant="contained"
                size="large"
                sx={{
                  bgcolor: theme.palette.background.paper,
                  color: theme.palette.primary.main,
                  px: 4,
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                  "&:hover": {
                    bgcolor: theme.palette.grey[100],
                  },
                }}
              >
                Get Started Free
              </Button>
              <Button
                component={Link}
                to="/Contactus"
                variant="outlined"
                size="large"
                sx={{
                  borderColor: theme.palette.primary.contrastText,
                  color: theme.palette.primary.contrastText,
                  px: 4,
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                  borderWidth: 2,
                  "&:hover": {
                    borderWidth: 2,
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                Watch Demo
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Benefits Bar */}
      <Box
        sx={{
          bgcolor:
            theme.palette.mode === "dark"
              ? theme.palette.grey[900]
              : theme.palette.grey[50],
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 4,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ color: theme.palette.primary.main }}>
                    {benefit.icon}
                  </Box>
                  <Typography
                    sx={{ fontWeight: 500, color: theme.palette.text.primary }}
                  >
                    {benefit.text}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: "bold",
                color: theme.palette.text.primary,
                mb: 2,
              }}
            >
              Everything You Need to Manage Invoices
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: "800px",
                mx: "auto",
              }}
            >
              Our comprehensive invoicing solution comes packed with powerful
              features to streamline your business operations and impress your
              clients.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    p: 2,
                    transition: "all 0.3s",
                    "&:hover": {
                      boxShadow: theme.shadows[8],
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? theme.palette.primary.dark
                            : `${theme.palette.primary.main}15`,
                        color: theme.palette.primary.main,
                        width: 72,
                        height: 72,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 3,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontWeight: "bold",
                        color: theme.palette.text.primary,
                        mb: 1.5,
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.text.secondary,
                        lineHeight: 1.7,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Payment Methods Section */}
      <Box
        sx={{
          bgcolor:
            theme.palette.mode === "dark"
              ? theme.palette.grey[900]
              : theme.palette.grey[50],
          py: 10,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: "bold",
                color: theme.palette.text.primary,
                mb: 2,
              }}
            >
              Flexible Payment Options
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: theme.palette.text.secondary }}
            >
              Accept payments the way your customers prefer
            </Typography>
          </Box>

          <Paper elevation={3} sx={{ p: 6, borderRadius: 3 }}>
            <Grid container spacing={4}>
              {paymentMethods.map((method, index) => (
                <Grid item xs={6} md={3} key={index}>
                  <Box sx={{ textAlign: "center" }}>
                    <Box
                      sx={{
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? `${method.color}30`
                            : `${method.color}15`,
                        width: 96,
                        height: 96,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 2,
                        color: method.color,
                      }}
                    >
                      {method.icon}
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {method.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: theme.palette.primary.contrastText,
          py: 10,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{ fontWeight: "bold", mb: 3 }}
            >
              Ready to Revolutionize Your Invoicing?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Join thousands of businesses that trust our platform for their
              invoicing needs. Get started in minutes, no credit card required.
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                component={Link}
                to="/login"
                variant="contained"
                size="large"
                sx={{
                  bgcolor: theme.palette.background.paper,
                  color: theme.palette.primary.main,
                  px: 4,
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                  "&:hover": {
                    bgcolor: theme.palette.grey[100],
                  },
                }}
              >
                Start Free Trial
              </Button>
              <Button
                 component={Link}
               to="/Contactus"
                variant="outlined"
                size="large"
                sx={{
                  borderColor: theme.palette.primary.contrastText,
                  color: theme.palette.primary.contrastText,
                  px: 4,
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                  borderWidth: 2,
                  "&:hover": {
                    borderWidth: 2,
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                Contact Sales
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Trust Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: "bold",
                    color: theme.palette.primary.main,
                    mb: 1,
                  }}
                >
                  10,000+
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Active Users
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: "bold",
                    color: theme.palette.primary.main,
                    mb: 1,
                  }}
                >
                  500K+
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Invoices Generated
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: "bold",
                    color: theme.palette.primary.main,
                    mb: 1,
                  }}
                >
                  99.9%
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Uptime
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Layout>
  );
};

export default EinvoiceInfo;
